import { ChevronDown, Search, X } from "lucide-react";
import { useState } from "react";

/**
 * A multi-select chip with a search box, as the mockup's Group and POP pickers do.
 * `selected` empty means no filter: everything passes.
 */
export function Picker({
  label,
  options,
  selected,
  onChange,
  flag,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  flag?: (option: string) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const matching = options.filter((o) => o.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={`chip${selected.length ? " chip-active" : ""}`} onClick={() => setOpen((o) => !o)}>
          {label}
          {selected.length > 0 && <span className="num">{selected.length}</span>}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        {selected.length > 0 && (
          <button type="button" className="text-xs font-semibold" style={{ color: "#ed125f" }} onClick={() => onChange([])}>
            Clear
          </button>
        )}
      </div>

      {open && (
        <div className="custom-scrollbar absolute left-0 top-9 z-30 max-h-72 w-72 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          {/* ⚠ A search box inside the dropdown — Hugues asked for exactly this on the POP
              filter of Order Activation, and 74 POPs is unusable without one. */}
          <div className="relative mb-2">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
              <Search className="h-3.5 w-3.5" />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}`}
              className="field field-sm field-icon-sm"
              autoFocus
            />
            {query && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setQuery("")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {matching.map((option) => (
            <label key={option} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-slate-50">
              <input
                type="checkbox"
                className="tick"
                checked={selected.includes(option)}
                onChange={() =>
                  onChange(selected.includes(option) ? selected.filter((s) => s !== option) : [...selected, option])
                }
              />
              <span className="flex-1 truncate text-slate-600">{option}</span>
              {flag?.(option) && <span className="pill pill-amber">Extra</span>}
            </label>
          ))}
          {matching.length === 0 && <p className="px-1.5 py-2 text-xs text-slate-400">Nothing matches.</p>}
        </div>
      )}
    </div>
  );
}
